import { Resource } from "./resource";
import { RevisionItem } from "./resources/RevisionItem";
import { FullResourceActions, createFullResource } from "./fullResource";
import { Decision } from "./resources/Decision";
import { ListDto } from "./list";

interface Draft {
  id: number;
  target: string;
  createdAt: string;
  draftName: string;
}

export interface RevisionActions<
  TDetail extends RevisionItem = any,
  TSimple = any,
> extends FullResourceActions<TDetail, TSimple> {
  duplicateItem(item: TDetail): Promise<TDetail>;
  createStartingDecision(
    itemId: string | number,
    decision: Decision,
  ): Promise<Decision>;
  fetchDrafts(): Promise<ListDto<Draft>>;
}

export function createRevisionResource<TDetail extends RevisionItem, TSimple>(
  apiUrl: string,
  detailDataTransform: (data: any) => TDetail,
  simpleDataTransform: (data: any) => TSimple = (data) => data,
): Resource<RevisionActions<TDetail, TSimple>> {
  return (client) => ({
    ...createFullResource(
      apiUrl,
      detailDataTransform,
      simpleDataTransform,
    )(client),
    async duplicateItem(item) {
      const resp = await client.post(
        `${apiUrl}${item.id}/duplicate/`,
        undefined,
      );

      return detailDataTransform(resp.data);
    },
    async createStartingDecision(itemId, decision) {
      const data = decision.getPostData();
      const resp = await client.post(
        `${apiUrl}${itemId}/create_activation_decision/`,
        data,
      );
      const DecisionCtor = (await import("./resources/Decision")).Decision;

      return new DecisionCtor(resp.data);
    },
    async fetchDrafts() {
      const response = await client.get(`${apiUrl}drafts/`);
      return response.data;
    },
  });
}

export const createRevisionClassResource = <
  TDetail extends RevisionItem,
  TSimple,
>(
  apiUrl: string,
  constructor: new (data: any) => TDetail,
  simpleDataTransform: (data: any) => TSimple = (data) => data,
) =>
  createRevisionResource(
    apiUrl,
    (data) => new constructor(data),
    simpleDataTransform,
  );
